import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { PanelHeader } from './PanelHeader';
import type { ButtonColor } from './Button';

interface DrawerProps {
  children: ReactNode;
  title: string;
  widthClassName?: string;
  accent?: ButtonColor | undefined;
  footer?: ReactNode;
  onClose: () => void;
}

export const Drawer = ({
  children,
  title,
  widthClassName = 'max-w-md',
  accent,
  footer,
  onClose,
}: DrawerProps) => {
  const { t } = useTranslation();

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return createPortal(
    <div aria-modal="true" className="fixed inset-0 z-50" role="dialog">
      <button
        aria-label={t('modal.closeModal')}
        className="animate-backdrop-in absolute inset-0 cursor-default bg-white/35 backdrop-blur-md"
        onClick={onClose}
        type="button"
      />
      <div
        className={`animate-drawer-in absolute inset-y-0 right-0 flex w-full flex-col border-l border-surface-tint bg-white shadow-modal ${widthClassName}`}
      >
        <PanelHeader
          className="px-5 pb-3 pt-[calc(1.5rem+env(safe-area-inset-top))]"
          closeColor={accent}
          title={title}
          onClose={onClose}
        />
        <div className="grid flex-1 content-start gap-4 overflow-y-auto px-5 pb-5">{children}</div>
        {footer ? (
          <div className="grid gap-2 border-t border-surface-tint p-5">{footer}</div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
};
